/**
 * RemoteConfig
 */

import { FirebaseOptions } from "firebase/app";

// Constants
const FIREBASE_DATABASE_ID_DEV = "isimcha-apps-01-dev";
// const FIREBASE_DATABASE_ID_PROD = "(default)";
// These should match the GitHub secrets of the same name.
const PAYLOAD_PATH1 = "/2b5d0cc8-d1bf-4e15-8ce5-cf2e8143425e/64dd420b-5fb3-4711-8903-b03df7d86218.json";
const PAYLOAD_PATH2 = "/2b5d0cc8-d1bf-4e15-8ce5-cf2e8143425e/23a048a0-015f-5377-b756-ea77ffcdbde3.json";

type UrlShortenerSettings = {
    provider: string;
    domain: string;
    path: string;
    api_key: string;
};

type RemoteConfigSettings = {
    application_url: string;
    together_ai: {
        api_key: string;
    };
    algolia: {
        api_key: string;
        application_id: string;
    };
    url_shortener: UrlShortenerSettings;
    amazon: {
        cognito: {
            identity_pool_id: string;
            region: string;
        };
    };
    websocket_server: {
        url: string;
    };
    google: {
        maps: {
            api_key: string;
        };
        firebase: {
            databaseId: string;
            config: FirebaseOptions;
        };
        google_analytics: {
            ga_tag: string;
        };
        recaptcha: {
            site_key: string;
        };
    };
    storage: {
        path: string;
    };
    feature: {
        sponsored_study: string;
    };
};

const ENV_PREFIX_NEW = "VITE_";

const getMetaEnv = () => {
    try {
        return (import.meta as ImportMeta).env as Record<string, string | boolean | undefined>;
    } catch (error) {
        return undefined;
    }
};

const readEnvValue = (variableName: string): string | undefined => {
    const envFromImportMeta = getMetaEnv();
    const metaValue = envFromImportMeta?.[variableName];
    if (typeof metaValue !== "undefined") {
        return String(metaValue);
    }

    if (typeof process !== "undefined" && process.env) {
        return process.env[variableName];
    }

    return undefined;
};

const isProdRuntime = () => {
    const metaEnv = getMetaEnv();
    if (typeof metaEnv?.PROD === "boolean") {
        return metaEnv.PROD;
    }
    return typeof process !== "undefined" ? process.env?.NODE_ENV === "production" : false;
};

/**
 * RemoteConfig class to load the config from a remote URL provided by Secrets Manager.
 */
export default class RemoteConfigService {
    // Use a singleton to prevent multiple instantiation calls.

    private static _instance: RemoteConfigService;

    // Path1 is the GitHub secret PAYLOAD_PATH1
    // Path2 is the GitHub secret PAYLOAD_PATH2
    private readonly _payload = {
        path1: PAYLOAD_PATH1,
        path2: PAYLOAD_PATH2,
    };

    private _prod = false;

    private _settings: Partial<RemoteConfigSettings>;

    private constructor(settings: Partial<RemoteConfigSettings>) {
        const devFlag = RemoteConfigService.getFromEnv(`${ENV_PREFIX_NEW}DEV`);
        this._prod = devFlag ? devFlag !== "true" : isProdRuntime();
        this._settings = settings;
    }

    public static async Instance() {
        if (!this._instance) {
            const settings = await RemoteConfigService.load();
            this._instance = new RemoteConfigService(settings);
        }
        return this._instance;
    }

    public get prod(): boolean {
        return this._prod;
    }

    public get settings(): object {
        if (!this._settings) {
            console.error("_settings is undefined");
            console.debug("_settings:", this._settings);
            // return new RemoteConfigSettings();
            // return {};
            return new RemoteConfigService({});
        }
        return this._settings;
    }

    // Getter for the Firebase database ID
    public get databaseId(): string {
        const databaseId = this._settings.google?.firebase?.databaseId;
        if (databaseId) {
            return databaseId;
        }
        console.error("databaseId is undefined");
        return "";
    }

    // Getter for the Firebase config
    public get firebaseOptions(): FirebaseOptions {
        const firebaseOptions = this._settings.google?.firebase?.config;
        if (firebaseOptions) {
            return firebaseOptions;
        }
        console.error("firebaseOptions is undefined");
        console.debug("Settings:", this._settings);
        return {};
    }

    public get mapsAPIKey(): string {
        const mapsAPIKey = this._settings.google?.maps?.api_key;
        if (mapsAPIKey) {
            return mapsAPIKey;
        }
        console.error("mapsAPIKey is undefined");
        return "";
    }

    public get urlShortener(): UrlShortenerSettings {
        if (!this._settings.url_shortener) {
            console.error("URL Shortener Settings are undefined");
            return <UrlShortenerSettings>{
                provider: "",
                domain: "",
                url: "",
                path: "",
                api_key: "",
            };
        }
        return this._settings.url_shortener;
    }

    public get algoliaAPIKey(): string {
        const algoliaAPIKey = this._settings.algolia?.api_key;
        if (algoliaAPIKey) {
            return algoliaAPIKey;
        }
        console.error("algoliaAPIKey is undefined");
        return "";
    }

    public get algoliaApplicationId(): string {
        const algoliaApplicationId = this._settings.algolia?.application_id;
        if (algoliaApplicationId) {
            return algoliaApplicationId;
        }
        console.error("algoliaApplicationId is undefined");
        return "";
    }

    public get amazonCognitoId(): string {
        const amazonCognitoId = this._settings.amazon?.cognito?.identity_pool_id;
        if (amazonCognitoId) {
            return amazonCognitoId;
        }
        console.error("amazonCognitoId is undefined");
        return "";
    }

    public get googleAnalyticsTag(): string {
        const gaTag = this._settings.google?.google_analytics?.ga_tag;
        if (gaTag) {
            return gaTag;
        }
        console.error("Google Analytics Tag is undefined");
        return "";
    }

    public get recaptchaSiteKey(): string {
        const siteKey = this._settings.google?.recaptcha.site_key;
        if (siteKey) {
            return siteKey;
        }
        console.error("ReCaptcha site key is undefined");
        return "";
    }

    public get amazonCognitoRegion(): string {
        const amazonCognitoRegion = this._settings.amazon?.cognito?.region;
        if (amazonCognitoRegion) {
            return amazonCognitoRegion;
        }
        console.error("amazonCognitoRegion is undefined");
        return "";
    }

    public get sponsorFeatureFlag(): string {
        const sponsorFeatureFlag = this._settings.feature?.sponsored_study;
        if (sponsorFeatureFlag) {
            return sponsorFeatureFlag;
        }
        console.error("sponsorFeatureFlag is undefined");
        return "";
    }

    public get websocketUrl(): string {
        const websocketUrl = this._settings.websocket_server?.url;
        if (websocketUrl) {
            return websocketUrl;
        }
        console.error("websocketUrl is undefined");
        return "";
    }

    private static getFromEnv(variableName: string): string {
        const variable = readEnvValue(variableName);
        if (typeof variable !== "undefined") {
            return variable;
        }
        console.error("Error! Environment variable %s is not set. Using empty string.", variableName);
        return "";
    }

    private static async load(): Promise<Partial<RemoteConfigSettings>> {
        const devFlag = RemoteConfigService.getFromEnv(`${ENV_PREFIX_NEW}DEV`);
        const isProd = devFlag ? devFlag !== "true" : isProdRuntime();
        if (isProd) {
            return RemoteConfigService.loadUrl(PAYLOAD_PATH2);
        }

        return {
            application_url: RemoteConfigService.getFromEnv("VITE_URL"),
            google: {
                maps: {
                    api_key: RemoteConfigService.getFromEnv("VITE_GOOGLE_MAPS_API_KEY"),
                },
                firebase: {
                    databaseId: FIREBASE_DATABASE_ID_DEV,
                    config: JSON.parse(RemoteConfigService.getFromEnv("VITE_FIREBASE_CONFIG") || "{}"),
                },
                google_analytics: {
                    ga_tag: RemoteConfigService.getFromEnv("VITE_GOOGLE_ANALYTICS_TAG"),
                },
                recaptcha: {
                    site_key: RemoteConfigService.getFromEnv("VITE_RECAPTCHA_SITE_KEY"),
                },
            },
            url_shortener: {
                provider: "Kutt",
                domain: RemoteConfigService.getFromEnv("VITE_URL_SHORTENER_DOMAIN_STAGING"),
                path: "/api/v2/links/",
                api_key: RemoteConfigService.getFromEnv("VITE_URL_SHORTENER_API_KEY_STAGING"),
            },
            websocket_server: {
                url: RemoteConfigService.getFromEnv("VITE_WEBSOCKET_URL"),
            },
            algolia: {
                api_key: RemoteConfigService.getFromEnv("VITE_ALGOLIA_SEARCH"),
                application_id: RemoteConfigService.getFromEnv("VITE_ALGOLIA_APPLICATION_ID"),
            },
            amazon: {
                cognito: {
                    identity_pool_id: RemoteConfigService.getFromEnv("VITE_AMAZON_COGNITO_IDENTITY_POOL_ID"),
                    region: RemoteConfigService.getFromEnv("VITE_AMAZON_COGNITO_IDENTITY_POOL_ID").split(":")[0],
                },
            },
        };
    }

    static async loadUrl(url: string): Promise<Partial<RemoteConfigSettings>> {
        try {
            const resp = await fetch(url, {
                method: "GET",
                headers: {
                    Accept: "application/json",
                },
            }).then((response) => response.json());

            if (!("data" in resp)) {
                console.error("'data' is not in JSON response");
                console.debug(resp);
                return {};
            }

            // This takes the encoded payload from the secret manager, decodes the payload using base64,
            // then reverses the string finally one more round decoding is done to obtain the actual CONFIG_PAYLOAD
            // values required by the application.
            // So steps followed:
            // 1. dataJson.data is base64 decoded
            // 2. reverse the base64 decoded data
            // 3. the reversed string is base64 decoded
            const settings = window.atob(window.atob(resp.data).split("").reverse().join(""));
            // pass this JSON string as arg; i.e. payload is a JSON string.
            // console.debug("JSON:", JSON.parse(settings));
            return JSON.parse(settings);
        } catch (e) {
            console.error(e);
            return {};
        }
    }
}
